import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { EQMenuButton } from "./EQMenuButton";
import { EQWidget } from "./Modals";
import { useAppSelector, useEQDispatch, useEQSelector, useModal } from "src/Hooks";
import { EQModalStates, EQStateType, EQStates, PointType, ReturnEQViewStates, ReturnViewMode } from "./";
import { EQueueConfigType, EQueueType, WidgetType } from "src/Types/ReDoc/EQueue";
import ReturnWidget, { ReturnWidgetDisplayMode } from "src/Views/EQueue/Widgets/ReturnWidget/ReturnWidget";
import { getLearningMode } from "src/Slicers/panelSlicer";
import { autoUpdate, returnAutoUpdate, shouldExitAfterAction } from "./eQueueUtils";
import { useReturnTimers } from "./Hooks/useReturnTimers";
import { useEQActions } from "./Hooks/useEQActions";
import { useInactivityTimer } from "./Hooks/useInactivityTimer";
import { useCalledActivityDetector } from "./Hooks/useCalledActivityDetector";
import { EQueueModals } from "./EQueueModals";
import { buildReturnMenuStatusBadge } from "./returnMenuStatusBadge";
import { applyReturnFeatureFlags } from "./returnEqFeatureFlags";
import { getPauseTotalStorageKey } from "./pauseTimeStorage";
import "./eQueue.scss";

interface IEnterEQProps {
	isExpanded?: boolean;
	isNeedLogout?: boolean;
	type: EQueueType;
}

export const EQueue: React.FC<IEnterEQProps> = ({ isExpanded, type, isNeedLogout = false }) => {

	const inLearningMode = useAppSelector( getLearningMode );
	const userName = useAppSelector( state => state.auth.userName );
	const [ showWidget, setShowWidget ] = useState<boolean>(false);
	const [ widgetPosition, setWidgetPosition ] = useState<PointType|null>(null);
	const { showError } = useModal();

	const {
		dispatch, dispatchStd, getConfig, getQueueState, getStatus, saveConfig, start, call, getQuery, delay, redirect,
		beginServing, redirectWindow, getWindows, recall, cancelCall, endPause, beginPause, beginBreakFromCalling,
		replaceSelected, callById, drop, getDenyReasons, reason, endServing, exit, previewNext, confirmCall
	} = useEQDispatch( type );

	const queueUI = {
		status: useEQSelector<"idle" | "pending" | "succeeded" | "failed">( type, "loading" ),
		state: useEQSelector<WidgetType>( type, "widgetState") || 0,
		modalState: useEQSelector<EQModalStates>( type, "modalState" ),
		queueCount: useEQSelector<number>( type, "allCount" ) || 0,
		statusData: useEQSelector<{ ticket?: { id?: string; idQ?: string } }>( type, "status" )
	};
	const idQ = queueUI.statusData?.ticket?.idQ || queueUI.statusData?.ticket?.id || "";

	const returnUI = {
		viewMode: useEQSelector<ReturnViewMode>( type, "viewMode" ),
		config: useEQSelector<EQueueConfigType>( type, "config" ),
		windows: useEQSelector<Array<{ id: string; text: string }>>( type, "windows" ) || [],
		windowsStatus: useEQSelector<"idle" | "loading" | "loaded">( type, "windowsStatus" ) || "idle",
		windowsError: useEQSelector<string | undefined>( type, "error" ),
		ticket: useEQSelector<{ id?: string; number?: string; description?: string; inviteTime?: string; startTime?: string; customerName?: string; comment?: string } | undefined>( type, "ticket" ),
		pauseStartTime: useEQSelector<string | undefined>( type, "pauseStartTime" ),
		status: useEQSelector<{ message?: string } | undefined>( type, "status" ),
		queueCount: useEQSelector<number>( type, "queueCount" ) || 0,
		deferredCount: useEQSelector<number>( type, "queuePersonCount" ) || 0,
		transferedCount: useEQSelector<number>( type, "transferedCount" ) || 0
	};

	const isReturnQueue = type === EQueueType.Return;
	const currentTicketId = isReturnQueue ? returnUI.ticket?.id || idQ : idQ;

	const pauseTotalStorageKey = isReturnQueue
		? getPauseTotalStorageKey(returnUI.config)
		: undefined;

	const { pauseElapsed, pauseTotalElapsed, callElapsed, serviceElapsed } = useReturnTimers({
		isReturnQueue,
		viewMode: returnUI.viewMode,
		pauseStartTime: returnUI.pauseStartTime,
		pauseTotalStorageKey,
		ticket: returnUI.ticket
	});

	useInactivityTimer({
		isActive: isReturnQueue && returnUI.viewMode !== "inactive" && returnUI.viewMode !== "pause",
		onExpire: () => exit(idQ)
	});

	useCalledActivityDetector({
		isActive: isReturnQueue && returnUI.viewMode === "called",
		onConfirm: () => beginServing()
	});

	const { onActionHandler, onCloseModal, setPostCompleteAction } = useEQActions({
		type, isReturnQueue, idQ: currentTicketId,
		config: returnUI.config,
		queueCount: queueUI.queueCount,
		showError, setShowWidget,
		dispatch, saveConfig, getStatus, getQuery, call, previewNext, confirmCall, callById,
		beginPause, beginBreakFromCalling, replaceSelected, endPause, drop,
		recall, cancelCall, beginServing, delay, redirect, redirectWindow,
		getDenyReasons, reason, endServing, exit, start, getWindows
	});

	/** При изменении режима обучения скрываем виджет */
	useEffect( () => {
		setShowWidget( prev => inLearningMode ? false : prev );
	}, [ inLearningMode ] );

	/** Принудительный выход при isNeedLogout */
	useEffect( () => {
		if ( !isNeedLogout ) return;
		if ( type === EQueueType.Return ) { exit(idQ); return; }
		switch ( queueUI.state ) {
			case WidgetType.None: return;
			case WidgetType.Waiting:
				exit(idQ);
				break;
			case WidgetType.Calling:
				Promise.resolve( drop(idQ || "") ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			case WidgetType.Working:
				Promise.resolve( endServing(idQ || "") ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			case WidgetType.OnPause:
				Promise.resolve( endPause() ).then( res => { if ( shouldExitAfterAction(res) ) exit(idQ); } );
				break;
			default:
				console.warn( `Неизвестное состояние ЭО - ${WidgetType[queueUI.state]} (${queueUI.state}).` );
		}
	// eslint-disable-next-line
	}, [ isNeedLogout ] );

	/** Первичная и периодическая синхронизация с backend */
	useEffect( () => {
		getConfig();
		if ( isReturnQueue ) getStatus();
		getQueueState();
		const timerID = setInterval( getQueueState, isReturnQueue ? returnAutoUpdate : autoUpdate );
		return () => clearInterval(timerID);
	}, [] );

	/** При смене состояния разворачиваем виджет (или сворачиваем для не-return) */
	useEffect( () => {
		if ( type === EQueueType.Return ) {
			if ( queueUI.state !== 0 && !showWidget ) setShowWidget(true);
			return;
		}
		if ( queueUI.state !== 0 && !showWidget ) setShowWidget(true);
		else if ( queueUI.state === 0 ) setShowWidget(false);
	}, [ queueUI.state ] );

	/** Затемнение контента при открытом виджете или модалке */
	useEffect( () => {
		dispatchStd( { type: "waiting/setBlockContent", payload: ( showWidget || queueUI.modalState !== 0 ) } );
	}, [ showWidget, queueUI.modalState ] );

	const onLoginHandler = ( isSuccess: boolean ) => {
		if ( isSuccess ) {
			onCloseModal();
			if ( !isExpanded ) setWidgetPosition({ left: 104, top: 700 });
			setShowWidget(true);
		}
	};

	const onOpenWidgetHandler = ( position: PointType ) => {
		if ( queueUI.state !== 0 ) {
			setWidgetPosition( isExpanded ? null : { ...position, left: 104 } );
			setShowWidget( !showWidget );
		} else if ( isReturnQueue ) {
			setWidgetPosition( isExpanded ? null : { ...position, left: 104 } );
			setShowWidget(!showWidget);
		} else {
			dispatch( "openLoginForm" );
		}
	};

	const onCloseWidgetHandler = () => { if ( !isExpanded || isReturnQueue ) setShowWidget(false); };

	const getReturnWidgetDisplayMode = (): ReturnWidgetDisplayMode => {
		if (returnUI.viewMode === "pause") return "pause";
		if (returnUI.viewMode === "free") return "queue";
		return "ticket";
	};

	const renderInMenu = () => {
		const currentState = ( EQStates[queueUI.state] || EQStates[WidgetType.Unknown] ) as EQStateType;
		const returnStateBase = ReturnEQViewStates[returnUI.viewMode || "inactive"] || ReturnEQViewStates.inactive;
		const returnStateWithFlags = isReturnQueue
			? applyReturnFeatureFlags(returnStateBase, returnUI.config)
			: currentState;
		const isCompactReturnState = isReturnQueue && (
			(returnUI.viewMode === "free" && returnStateWithFlags.actions?.length === 3) ||
			(returnUI.viewMode === "confirming" && returnStateWithFlags.actions?.length === 2)
		);
		const returnState = isCompactReturnState
			? { ...returnStateWithFlags, compact: true }
			: returnStateWithFlags;

		const returnMenuStatusBadge = isReturnQueue
			? buildReturnMenuStatusBadge(returnUI.viewMode || "inactive", returnState, {
				queueCount: queueUI.queueCount,
				ticketNumber: returnUI.ticket?.number,
				pauseElapsed,
				pauseTotalElapsed,
				callElapsed,
				serviceElapsed
			})
			: undefined;

		const menuBtn = (
			<EQMenuButton
				isActive={!inLearningMode}
				color={isReturnQueue ? (returnState.borderTopColor || returnState.color) : currentState.color}
				isWait={queueUI.status === "pending"}
				isExpanded={isExpanded}
				title={isReturnQueue ? (returnState.text || "Очередь") : "Очередь"}
				iconClass='icon-electronic_queue'
				statusBadge={returnMenuStatusBadge}
				onClick={onOpenWidgetHandler}
			/>
		);

		if ( !showWidget ) return menuBtn;

		if ( isReturnQueue ) {
			const returnWidgetQueueCount = queueUI.queueCount;

			return createPortal(
				<ReturnWidget
					state={returnState}
					displayMode={getReturnWidgetDisplayMode()}
					userName={userName}
					queueCount={returnWidgetQueueCount}
					deferredCount={returnUI.deferredCount}
					transferedCount={returnUI.transferedCount}
					windows={returnUI.windows}
					windowsStatus={returnUI.windowsStatus}
					windowsError={returnUI.windowsError}
					ticketNumber={returnUI.viewMode === "pause" ? pauseElapsed : returnUI.ticket?.number}
					restTime={returnUI.viewMode === "pause" ? pauseTotalElapsed : returnUI.viewMode === "called" ? callElapsed : returnUI.viewMode === "working" ? serviceElapsed : undefined}
					ticketDescription={returnUI.viewMode === "pause" ? "" : returnUI.ticket?.description}
					ticketCustomerName={returnUI.viewMode === "working" ? returnUI.ticket?.customerName : undefined}
					actions={returnState.actions}
					isWaiting={false}
					onClose={onCloseWidgetHandler}
					onAction={onActionHandler}
					comment={returnUI.ticket?.comment}
				/>,
				document.body
			);
		}

		const widget = <EQWidget type={type} position={widgetPosition} onClose={onCloseWidgetHandler} state={currentState} onAction={onActionHandler} />;
		if ( !!isExpanded ) return <li className="menu_item">{widget}</li>;
		return createPortal( widget, document.body );
	};

	return (
		<>
			<div onMouseDown={(e) => e.stopPropagation()}>
				<EQueueModals
					modalState={queueUI.modalState}
					type={type}
					onAction={onActionHandler}
					onClose={onCloseModal}
					onLogin={onLoginHandler}
					setShowWidget={setShowWidget}
					showError={showError}
					setPostCompleteAction={setPostCompleteAction}
				/>
			</div>
			{ renderInMenu() }
		</>
	);
};
